<template>
  <v-container fluid class="pa-0">
    <v-sheet color="primary" class="py-8 mb-8" rounded="0" elevation="1">
      <v-row justify="center" class="text-center mt-2">
        <v-icon icon="mdi-tune-variant" size="56" color="white" class="mb-4"></v-icon>
        <h1 class="text-h3 font-weight-light text-white">{{ t('personaframework.title') }}</h1>
      </v-row>
      <v-row justify="center" class="text-center">
        <div class="text-subtitle-1 text-white mt-2">{{ t('personaframework.description') }}</div>
      </v-row>
    </v-sheet>

    <div class="d-flex flex-wrap justify-space-between align-center mb-4 mx-8 ga-4">
      <v-btn
          variant="text"
          prepend-icon="mdi-arrow-left"
          color="primary"
          @click="goBack"
      >
        {{ t('personaframework.backToPersonaManagement') }}
      </v-btn>

      <div class="d-flex align-center ga-4">
        <v-select
            class="domain-select"
            :label="$t('generic.domain')"
            :items="domainOptions"
            item-title="title"
            item-value="value"
            v-model="selectedDomain"
            @update:modelValue="onDomainChange"
            density="default"
            variant="outlined"
            color="primary"
            prepend-inner-icon="mdi-domain"
            hide-details
            style="min-width: 220px;"
        />
        <v-btn color="primary" prepend-icon="mdi-plus" @click="openAddAttributeDialog">
          {{ t('personaframework.addAttribute') }}
        </v-btn>
      </div>
    </div>

    <v-row class="mx-4">
      <v-col cols="12">
        <v-card variant="outlined" rounded="lg" class="pa-6">
          <div class="mb-4">
            <div class="text-h6 font-weight-bold">{{ t('personaframework.attributeManagement') }}</div>
            <div class="text-body-2 text-medium-emphasis">
              {{ t('personaframework.attributeManagementHint') }}
            </div>
            <div class="text-caption text-medium-emphasis mt-1">
              <v-icon icon="mdi-cursor-move" size="14" class="mr-1" />{{ t('personaframework.dragReorderHint') }}
            </div>
          </div>

          <!-- Manual header row + a single scrollable draggable list (no
               pagination), so a long domain (e.g. Telco, 19 attributes) never
               needs a page boundary crossed mid-drag. -->
          <div class="attributes-list-header d-flex align-center">
            <div class="col-drag"></div>
            <div class="col-order">{{ t('personaframework.columnOrder') }}</div>
            <div class="col-name">{{ t('personaframework.columnName') }}</div>
            <div class="col-type">{{ t('personaframework.columnType') }}</div>
            <div class="col-required">{{ t('personaframework.columnRequired') }}</div>
            <div class="col-editable">{{ t('personaframework.columnEditable') }}</div>
            <div class="col-deletable">{{ t('personaframework.columnDeletable') }}</div>
            <div class="col-options">{{ t('personaframework.columnOptions') }}</div>
            <div class="col-actions text-center">{{ t('generic.actions') }}</div>
          </div>

          <div v-if="loading" class="d-flex justify-center py-8">
            <v-progress-circular indeterminate color="primary" />
          </div>

          <div v-else-if="!sortedAttributes.length" class="text-center text-medium-emphasis py-8">
            {{ t('personaframework.noAttributes') }}
          </div>

          <div v-else class="attributes-scroll-container">
            <draggable
                v-model="sortableAttributes"
                item-key="attribute_id"
                handle=".drag-handle"
                animation="200"
                ghost-class="drag-ghost"
                drag-class="drag-active"
            >
              <template #item="{ element: item }">
                <div class="attributes-row d-flex align-center">
                  <div class="col-drag">
                    <v-icon
                        class="drag-handle"
                        icon="mdi-drag-vertical"
                        size="22"
                        :aria-label="t('personaframework.dragHandle')"
                    />
                  </div>
                  <div class="col-order">
                    <span class="order-badge">{{ item.display_order }}</span>
                  </div>
                  <div class="col-name">
                    <div class="font-weight-medium">{{ item.label }}</div>
                    <div class="text-caption text-medium-emphasis">{{ item.key }}</div>
                  </div>
                  <div class="col-type">
                    <v-chip size="small" :color="typeColor(item.type)" variant="tonal">
                      {{ typeLabel(item.type) }}
                    </v-chip>
                  </div>
                  <div class="col-required">
                    <v-switch
                        v-model="item.required"
                        color="primary"
                        hide-details
                        density="compact"
                        :disabled="item.is_system"
                    />
                  </div>
                  <div class="col-editable">
                    <v-tooltip :disabled="!item.is_system" :text="t('personaframework.systemAttributeLocked')" location="top">
                      <template v-slot:activator="{ props }">
                        <span v-bind="props">
                          <v-switch
                              v-model="item.editable"
                              color="primary"
                              hide-details
                              density="compact"
                              :disabled="item.is_system"
                          />
                        </span>
                      </template>
                    </v-tooltip>
                  </div>
                  <div class="col-deletable">
                    <v-tooltip :disabled="!item.is_system" :text="t('personaframework.systemAttributeLocked')" location="top">
                      <template v-slot:activator="{ props }">
                        <span v-bind="props">
                          <v-switch
                              v-model="item.deletable"
                              color="primary"
                              hide-details
                              density="compact"
                              :disabled="item.is_system"
                          />
                        </span>
                      </template>
                    </v-tooltip>
                  </div>
                  <div class="col-options">
                    <span class="text-body-2">{{ formatOptions(item) }}</span>
                  </div>
                  <div class="col-actions d-flex justify-center ga-1">
                    <v-tooltip :disabled="!item.is_system" :text="t('personaframework.systemAttributeLocked')" location="top">
                      <template v-slot:activator="{ props }">
                        <span v-bind="props">
                          <v-btn icon="mdi-pencil-outline" variant="text" size="small" color="primary" :disabled="item.is_system" @click="openEditAttributeDialog(item)" />
                        </span>
                      </template>
                    </v-tooltip>
                    <v-tooltip
                        :disabled="canDeleteAttribute(item)"
                        :text="item.is_system ? t('personaframework.systemAttributeLocked') : t('personaframework.attributeNotDeletable')"
                        location="top"
                    >
                      <template v-slot:activator="{ props }">
                        <span v-bind="props">
                          <v-btn
                              icon="mdi-delete-outline"
                              variant="text"
                              size="small"
                              color="error"
                              :disabled="!canDeleteAttribute(item)"
                              @click="openDeleteDialog(item)"
                          />
                        </span>
                      </template>
                    </v-tooltip>
                  </div>
                </div>
              </template>
            </draggable>
          </div>

          <div class="d-flex flex-wrap justify-space-between align-center mt-4 ga-3">
            <div class="text-body-2 text-medium-emphasis">
              {{ t('personaframework.attributeCount', { count: sortedAttributes.length }) }}
            </div>
            <v-btn color="primary" :loading="saving" :disabled="loading" @click="saveChanges">
              {{ t('personaframework.saveChanges') }}
            </v-btn>
          </div>
        </v-card>
      </v-col>
    </v-row>

    <!-- Add / Edit Attribute Dialog -->
    <v-dialog v-model="attributeDialog" max-width="560" persistent>
      <v-card>
        <v-card-title>{{ editingAttribute ? t('personaframework.editAttribute') : t('personaframework.addAttribute') }}</v-card-title>
        <v-card-text>
          <v-text-field
              v-model="attributeForm.label"
              :label="t('personaframework.fieldLabel')"
              variant="outlined"
              class="mb-3"
              @update:modelValue="syncKeyFromLabel"
          />
          <v-text-field
              v-model="attributeForm.key"
              :label="t('personaframework.fieldKey')"
              variant="outlined"
              class="mb-3"
          />
          <v-select
              v-model="attributeForm.type"
              :items="typeOptions"
              item-title="title"
              item-value="value"
              :label="t('personaframework.fieldType')"
              variant="outlined"
              class="mb-3"
          />
          <v-switch
              v-model="attributeForm.required"
              :label="t('personaframework.fieldRequired')"
              color="primary"
              class="mb-3"
          />
          <v-switch
              v-model="attributeForm.editable"
              :label="t('personaframework.fieldEditable')"
              color="primary"
              class="mb-3"
          />
          <v-switch
              v-model="attributeForm.deletable"
              :label="t('personaframework.fieldDeletable')"
              color="primary"
              class="mb-3"
          />

          <div v-if="attributeForm.type === 'number'" class="d-flex flex-column ga-3">
            <div class="d-flex ga-3">
              <v-text-field v-model.number="attributeForm.min" :label="t('personaframework.minValue')" type="number" variant="outlined" />
              <v-text-field v-model.number="attributeForm.max" :label="t('personaframework.maxValue')" type="number" variant="outlined" />
            </div>
            <v-combobox
              v-model="attributeForm.unit"
              :items="numberUnitSuggestions"
              :label="t('personaframework.unit')"
              :hint="t('personaframework.unitHint')"
              persistent-hint
              variant="outlined"
              clearable
            />
          </div>

          <v-combobox
              v-if="attributeForm.type === 'single_select'"
              v-model="attributeForm.choices"
              :label="t('personaframework.choices')"
              variant="outlined"
              multiple
              chips
              closable-chips
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="attributeDialog = false">{{ t('generic.cancel') }}</v-btn>
          <v-btn color="primary" :loading="attributeSaving" @click="saveAttributeDialog">
            {{ editingAttribute ? t('generic.save') : t('generic.add') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Attribute Dialog -->
    <v-dialog v-model="deleteDialog" max-width="480" :persistent="deletingAttribute">
      <v-card>
        <v-card-title>{{ t('personaframework.deleteAttribute') }}</v-card-title>
        <v-card-text>
          {{ deleteAttributeConfirmMessage }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" :disabled="deletingAttribute" @click="deleteDialog = false">{{ t('generic.cancel') }}</v-btn>
          <v-btn
            color="error"
            variant="tonal"
            :loading="deletingAttribute"
            @click="confirmDeleteAttribute"
          >
            {{ t('generic.delete') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="successSnackbar" color="success" location="bottom right" :timeout="3000">
      {{ successMessage }}
    </v-snackbar>
    <v-snackbar v-model="errorSnackbar" color="error" location="bottom right" :timeout="4000">
      {{ errorMessage }}
    </v-snackbar>
  </v-container>
</template>

<script>
import {
  build_domain_options,
  coerce_allowed_domain,
  get_default_domain,
  fetch_persona_domain_schema,
  save_persona_domain_schema,
  delete_persona_domain_attribute,
  fetch_persona_attribute_delete_impact,
  fetch_saved_personas_by_domain,
} from '@/api.js';
import { useI18n } from 'vue-i18n';
import { expandSelectChoices } from '@/utils/selectChoices';
import { countPersonasWithAttributeValue } from '@/utils/attributeImpact';
import { findAliasedAttributeKeyConflict } from '@/constants/filterSchema';
import { validatePersonaAttributeKey } from '@/utils/personaAttributeKey';
// Same library/pattern already used by PromptManagementTemplateForm.vue's
// step reordering.
import draggable from 'vuedraggable';

export default {
  name: 'PersonaFrameworkEditor',
  components: { draggable },
  setup() {
    const { t } = useI18n();
    return { t };
  },
  data() {
    return {
      selectedDomain: null,
      attributes: [],
      // attribute_id values last loaded from the backend schema (excludes
      // local-only discoveries like unsaved Country/Interests rows).
      persistedAttributeIds: new Set(),
      loading: false,
      saving: false,
      deletingAttribute: false,
      schemaLoadToken: 0,
      attributeDialog: false,
      attributeSaving: false,
      editingAttribute: null,
      attributeForm: this.emptyAttributeForm(),
      numberUnitSuggestions: ['cm', 'm', 'mm', 'kg', 'g', 'years', 'months', '%', '°C'],
      deleteDialog: false,
      attributeToDelete: null,
      deleteImpactCount: null,
      deleteImpactLoading: false,
      deleteImpactError: false,
      successSnackbar: false,
      successMessage: '',
      errorSnackbar: false,
      errorMessage: '',
    };
  },
  computed: {
    domainOptions() {
      return build_domain_options(this.$t);
    },
    sortedAttributes() {
      return [...this.attributes].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    },
    // Writable computed used as the <draggable> v-model. The getter always
    // reflects the current display_order-sorted list; the setter receives
    // the SAME item objects in their new, user-dropped order
    // (vuedraggable/Sortable.js reorders the array but never clones the
    // items) and renumbers display_order to match — the same
    // renumber-the-whole-list-atomically approach confirmDeleteAttribute()
    // already uses, just triggered by a drop instead of a delete.
    sortableAttributes: {
      get() {
        return this.sortedAttributes;
      },
      set(newOrder) {
        this.normalizeDisplayOrders(newOrder);
      },
    },
    deleteAttributeConfirmMessage() {
      const name = this.attributeToDelete?.label
        || this.attributeToDelete?.key
        || 'this attribute';
      if (this.deleteImpactLoading && this.deleteImpactCount == null) {
        return this.t('personaframework.deleteAttributeImpactLoading');
      }
      if (this.deleteImpactError || this.deleteImpactCount == null) {
        return this.formatDeleteImpactMessage(
          'personaframework.deleteAttributeImpactError',
          { name },
        );
      }
      if (this.deleteImpactCount === 0) {
        return this.formatDeleteImpactMessage(
          'personaframework.deleteAttributeConfirmNoValues',
          { name },
        );
      }
      const count = this.deleteImpactCount;
      const translated = this.t(
        'personaframework.deleteAttributeConfirmWithValues',
        { name, count, n: count },
        count,
      );
      if (translated.includes('{count}') || translated.includes('{name}')) {
        const personaWord = count === 1 ? 'persona has' : 'personas have';
        return `Delete "${name}"? ${count} ${personaWord} a value for it. `
          + 'Their values will be removed (archived first so they stay recoverable).';
      }
      return translated;
    },
    typeOptions() {
      return [
        { title: this.t('personaframework.typeTextShort'), value: 'text_short' },
        { title: this.t('personaframework.typeNumber'), value: 'number' },
        { title: this.t('personaframework.typeSingleSelect'), value: 'single_select' },
      ];
    },
  },
  async mounted() {
    await this.initializeDomain();
  },
  watch: {
    '$route.params.domainId': {
      immediate: false,
      async handler(domainId) {
        if (!domainId) return;
        const normalized = String(domainId).toLowerCase();
        if (normalized !== this.selectedDomain) {
          this.selectedDomain = normalized;
          await this.loadSchema(normalized);
        }
      },
    },
  },
  methods: {
    async initializeDomain() {
      const fromRoute = (this.$route.params.domainId || this.$route.query.domain || '').toLowerCase();
      this.selectedDomain = coerce_allowed_domain(fromRoute || null);
      if (this.selectedDomain) {
        await this.loadSchema();
      }
    },
    emptyAttributeForm() {
      return {
        label: '',
        key: '',
        type: 'text_short',
        required: false,
        editable: true,
        deletable: true,
        min: null,
        max: null,
        unit: '',
        choices: [],
      };
    },
    canDeleteAttribute(item) {
      return !item.is_system && item.deletable !== false;
    },
    newAttributeId() {
      if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
      }
      return `attr_${Date.now()}_${Math.random().toString(16).slice(2)}`;
    },
    goBack() {
      this.$router.push({ name: 'persona-management', query: { domain: this.selectedDomain } });
    },
    async onDomainChange() {
      const domain = (this.selectedDomain || '').toLowerCase();
      if (!domain) return;
      await this.$router.replace({
        name: 'persona-framework-editor',
        params: { domainId: domain },
      });
      await this.loadSchema(domain);
    },
    isSchemaLoadCurrent(domain, loadToken) {
      return loadToken === this.schemaLoadToken
        && domain === (this.selectedDomain || '').toLowerCase();
    },
    async loadSchema(domainId = null) {
      const domain = (domainId || this.selectedDomain || '').toLowerCase();
      if (!domain) return;

      const loadToken = ++this.schemaLoadToken;
      this.loading = true;
      this.attributes = [];
      this.persistedAttributeIds = new Set();

      try {
        const response = await fetch_persona_domain_schema(domain);
        if (!this.isSchemaLoadCurrent(domain, loadToken)) return;

        const rows = response.data?.attributes ?? response.data ?? [];
        const mapped = (Array.isArray(rows) ? rows : []).map((attr) => {
          const isSystem = !!attr.is_system;
          return {
            ...attr,
            required: !!attr.required,
            // Missing on old/legacy-seeded documents (predating these fields):
            // system attrs keep fixed definitions but values stay editable.
            editable: attr.editable !== undefined ? !!attr.editable : true,
            deletable: attr.deletable !== undefined ? !!attr.deletable : !isSystem,
            is_system: isSystem,
            display_order: Number(attr.display_order) || 0,
            options: attr.options || null,
            previous_keys: Array.isArray(attr.previous_keys) ? [...attr.previous_keys] : [],
          };
        });

        // Stored display_order may already be gapped/out-of-range (e.g. legacy
        // data, or a direct API call that bypassed the app). Sort by the
        // stored value, using each row's original position in the response
        // as the tie-breaker for duplicates, then renumber to a clean 1..N.
        // This only updates in-memory editing state — nothing is persisted
        // until the user explicitly clicks Save Changes.
        const withOriginalIndex = mapped.map((attr, idx) => ({ attr, idx }));
        withOriginalIndex.sort((a, b) => {
          const diff = a.attr.display_order - b.attr.display_order;
          return diff !== 0 ? diff : a.idx - b.idx;
        });
        this.attributes = this.normalizeDisplayOrders(withOriginalIndex.map((entry) => entry.attr));
        this.persistedAttributeIds = new Set(
            this.attributes.map((attr) => attr.attribute_id).filter(Boolean)
        );

        // Country/Interests predate this attribute-schema system and were
        // never added to it, but personas already carry real values for
        // them (Interests in the backend persona documents; Country in
        // the static per-domain catalog PersonaGrid.vue reads it from —
        // see overlayBackendData there). Surface them here as ordinary,
        // editable/deletable/reorderable attributes so an admin can bring
        // them into the schema, without silently persisting anything —
        // they're only written once the admin clicks Save Changes, same
        // as any attribute added by hand.
        const discovered = await this.discoverUntrackedAttributes(domain, this.attributes);
        if (discovered.length && this.isSchemaLoadCurrent(domain, loadToken)) {
          discovered.forEach((item) => {
            this.attributes.push({
              attribute_id: this.newAttributeId(),
              display_order: this.attributes.length + 1,
              label: item.label,
              key: item.key,
              type: 'text_short',
              required: false,
              editable: true,
              deletable: true,
              options: null,
              is_system: false,
            });
          });
        }
      } catch (error) {
        if (!this.isSchemaLoadCurrent(domain, loadToken)) return;
        console.error('Failed to load persona domain schema:', error);
        this.showError(this.t('personaframework.loadError'));
        this.attributes = [];
        this.persistedAttributeIds = new Set();
      } finally {
        if (this.isSchemaLoadCurrent(domain, loadToken)) {
          this.loading = false;
        }
      }
    },
    // Checks whether this domain's real persona data has Country/Interests
    // values despite neither being in the stored schema yet. Country is
    // never in the backend persona documents themselves (confirmed by
    // inspecting the persona collection directly) — it only ever lived in
    // the static per-domain catalog — so that's checked too, matching
    // exactly what PersonaGrid.vue already treats as this domain's real
    // Country data. Returns only the ones missing from the schema AND
    // backed by a real value; never both key spellings for Interests
    // (personal_interests vs. Top Management's personalInterests) —
    // always reports the canonical personal_interests key, since
    // PersonaGrid's value resolution already checks both spellings.
    async discoverUntrackedAttributes(domain, attributes) {
      const hasKey = (keys) => attributes.some((attr) => keys.includes(attr.key));
      const needCountry = !hasKey(['country']);
      const needInterests = !hasKey(['personal_interests', 'personalInterests', 'interests']);
      if (!needCountry && !needInterests) return [];

      const discovered = [];
      try {
        const [manualRes, generatedRes] = await Promise.all([
          fetch_personas_by_domain(domain),
          fetch_generated_personas(domain),
        ]);
        const backendDocs = [...(manualRes.data || []), ...(generatedRes.data || [])];

        // A brand-new domain has no personas at all yet — there's nothing
        // to discover from, so it must keep showing only the default
        // attributes.
        if (!backendDocs.length) return [];

        // After N1, country lives on backend documents. Discover from API only.
        if (needCountry) {
          const hasCountryValue = backendDocs.some((d) => d.data && d.data.country);
          if (hasCountryValue) {
            discovered.push({ key: 'country', label: this.t('personaedit.fields.country') });
          }
        }
        if (needInterests) {
          const hasInterestsValue = backendDocs.some((d) => d.data && (
            (d.data.personal_interests && d.data.personal_interests.length)
            || (d.data.personalInterests && d.data.personalInterests.length)
          ));
          if (hasInterestsValue) {
            discovered.push({ key: 'personal_interests', label: this.t('personaedit.fields.interests') });
          }
        }
      } catch (error) {
        console.error('Failed to check for existing Country/Interests data:', error);
      }
      return discovered;
    },
    typeLabel(type) {
      const map = {
        text_short: this.t('personaframework.typeTextShort'),
        number: this.t('personaframework.typeNumber'),
        single_select: this.t('personaframework.typeSingleSelect'),
      };
      return map[type] || type;
    },
    typeColor(type) {
      if (type === 'number') return 'green';
      if (type === 'single_select') return 'purple';
      return 'blue';
    },
    formatOptions(item) {
      if (!item.options) return '—';
      if (item.type === 'number') {
        const min = item.options.min ?? item.options.min_value;
        const max = item.options.max ?? item.options.max_value;
        const unit = (item.options.unit || '').trim();
        if (min != null || max != null || unit) {
          const range = this.t('personaframework.numberRange', { min: min ?? '—', max: max ?? '—' });
          return unit ? `${range}, ${this.t('personaframework.unit')}: ${unit}` : range;
        }
      }
      if (item.type === 'single_select' && item.options.choices?.length) {
        return item.options.choices.join(', ');
      }
      return '—';
    },
    syncKeyFromLabel() {
      if (this.editingAttribute) return;
      this.attributeForm.key = (this.attributeForm.label || '')
          .trim()
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '_')
          .replace(/^_|_$/g, '');
    },
    openAddAttributeDialog() {
      this.editingAttribute = null;
      this.attributeForm = this.emptyAttributeForm();
      this.attributeDialog = true;
    },
    openEditAttributeDialog(item) {
      if (item.is_system) return;
      this.editingAttribute = item;
      this.attributeForm = {
        label: item.label,
        key: item.key,
        type: item.type,
        required: !!item.required,
        editable: item.editable !== false,
        deletable: item.deletable !== false,
        min: item.options?.min ?? item.options?.min_value ?? null,
        max: item.options?.max ?? item.options?.max_value ?? null,
        unit: item.options?.unit || '',
        choices: expandSelectChoices(item.options?.choices),
      };
      this.attributeDialog = true;
    },
    buildOptionsFromForm() {
      if (this.attributeForm.type === 'number') {
        const options = {};
        if (this.attributeForm.min != null && this.attributeForm.min !== '') options.min = Number(this.attributeForm.min);
        if (this.attributeForm.max != null && this.attributeForm.max !== '') options.max = Number(this.attributeForm.max);
        const unit = String(this.attributeForm.unit || '').trim();
        if (unit) options.unit = unit;
        return Object.keys(options).length ? options : null;
      }
      if (this.attributeForm.type === 'single_select') {
        const choices = expandSelectChoices(this.attributeForm.choices);
        return choices.length ? { choices } : null;
      }
      return null;
    },
    saveAttributeDialog() {
      if (!this.attributeForm.label?.trim() || !this.attributeForm.key?.trim()) {
        this.showError(this.t('personaframework.validationRequired'));
        return;
      }

      const keyResult = validatePersonaAttributeKey(this.attributeForm.key);
      if (!keyResult.ok) {
        this.showError(this.t('personaframework.validationInvalidKey'));
        return;
      }

      const payload = {
        label: this.attributeForm.label.trim(),
        key: keyResult.key,
        type: this.attributeForm.type,
        required: !!this.attributeForm.required,
        editable: !!this.attributeForm.editable,
        deletable: !!this.attributeForm.deletable,
        options: this.buildOptionsFromForm(),
      };

      const siblingKeys = this.attributes
        .filter((attr) => !this.editingAttribute || attr.attribute_id !== this.editingAttribute.attribute_id)
        .map((attr) => attr.key);
      const aliasConflict = findAliasedAttributeKeyConflict([...siblingKeys, payload.key]);
      if (aliasConflict) {
        this.showError(this.t('personaframework.duplicateAliasKeyError', {
          first: aliasConflict.keys[0],
          second: aliasConflict.keys[1],
        }));
        return;
      }

      if (this.editingAttribute) {
        const idx = this.attributes.findIndex((a) => a.attribute_id === this.editingAttribute.attribute_id);
        if (idx >= 0) {
          this.attributes.splice(idx, 1, {
            ...this.attributes[idx],
            ...payload,
          });
        }
      } else {
        this.attributes.push({
          attribute_id: this.newAttributeId(),
          display_order: this.attributes.length + 1,
          ...payload,
        });
      }

      this.attributeDialog = false;
    },
    formatDeleteImpactMessage(key, values) {
      const translated = this.t(key, values);
      const name = values?.name || 'this attribute';
      if (translated.includes('{name}')) {
        if (key === 'personaframework.deleteAttributeConfirmNoValues') {
          return `Delete "${name}"? No personas currently have a value for it.`;
        }
        return `Could not check how many personas are affected. Delete "${name}" anyway? `
          + 'Values will be removed from all personas in this domain '
          + '(archived first so they stay recoverable).';
      }
      return translated;
    },
    async countLocalAttributeImpact(domain, attributeKey, previousKeys = []) {
      const { data: personaDocs } = await fetch_saved_personas_by_domain(domain);
      return countPersonasWithAttributeValue(personaDocs, attributeKey, previousKeys);
    },
    async loadDeleteImpact(item) {
      const domain = (this.selectedDomain || '').toLowerCase();
      if (!domain || !item?.key) {
        this.deleteImpactError = true;
        this.deleteImpactCount = null;
        return;
      }

      const previousKeys = item.previous_keys || [];
      const isPersisted = this.persistedAttributeIds.has(item.attribute_id);

      // Count locally first so a failed delete-impact request (404, 401, etc.)
      // cannot clear auth or block the dialog from showing a persona count.
      let localCount = null;
      try {
        localCount = await this.countLocalAttributeImpact(domain, item.key, previousKeys);
        this.deleteImpactCount = localCount;
        this.deleteImpactError = false;
      } catch (localError) {
        console.error('Failed to count local attribute delete impact:', localError);
      }

      if (isPersisted) {
        try {
          const { data } = await fetch_persona_attribute_delete_impact(domain, item.attribute_id);
          if (typeof data?.personas_with_value_count === 'number') {
            this.deleteImpactCount = data.personas_with_value_count;
            this.deleteImpactError = false;
            return;
          }
        } catch (error) {
          console.error('Failed to load attribute delete impact from API:', error);
        }
      }

      if (localCount == null) {
        this.deleteImpactError = true;
        this.deleteImpactCount = null;
      }
    },
    async openDeleteDialog(item) {
      if (!this.canDeleteAttribute(item)) return;
      this.attributeToDelete = item;
      this.deleteImpactCount = null;
      this.deleteImpactError = false;
      this.deleteImpactLoading = true;
      this.deleteDialog = true;

      try {
        await this.loadDeleteImpact(item);
      } finally {
        this.deleteImpactLoading = false;
      }
    },
    async confirmDeleteAttribute() {
      if (!this.attributeToDelete || !this.canDeleteAttribute(this.attributeToDelete)) return;
      if (this.deletingAttribute) return;

      const domain = (this.selectedDomain || '').toLowerCase();
      const target = this.attributeToDelete;
      this.deletingAttribute = true;

      try {
        // Persisted attributes: hard-delete on the server (archive + purge
        // values from all personas in the domain, then drop from schema).
        // Local-only rows (unsaved discoveries / not-yet-saved adds) only
        // need to leave the in-memory list.
        let archivedPersonaCount = this.deleteImpactCount;
        if (domain && this.persistedAttributeIds.has(target.attribute_id)) {
          const { data } = await delete_persona_domain_attribute(domain, target.attribute_id);
          archivedPersonaCount = data?.archived_persona_count ?? archivedPersonaCount;
          this.persistedAttributeIds.delete(target.attribute_id);
        }

        // Renumber based on current display order (sortedAttributes), not raw
        // array/insertion order — otherwise a delete would silently discard
        // any moves the user made earlier in this editing session.
        const remaining = this.sortedAttributes.filter(
            (a) => a.attribute_id !== target.attribute_id
        );
        this.attributes = this.normalizeDisplayOrders(remaining);
        this.attributeToDelete = null;
        this.deleteImpactCount = null;
        this.deleteImpactError = false;
        this.deleteDialog = false;
        if (archivedPersonaCount > 0) {
          this.showSuccess(
            this.t('personaframework.deleteAttributeSuccess', { count: archivedPersonaCount }, archivedPersonaCount),
          );
        } else {
          this.showSuccess(this.t('personaframework.deleteAttributeSuccessNoValues'));
        }
      } catch (error) {
        const detail = error.response?.data?.detail;
        this.showError(
            typeof detail === 'string' ? detail : this.t('personaframework.deleteAttributeError')
        );
      } finally {
        this.deletingAttribute = false;
      }
    },
    // Renumbers a list to a clean 1..N based purely on its current element
    // order. Used after every reorder (drag-and-drop drop, delete) so
    // display_order can never end up with gaps or duplicates — each call
    // is a full, atomic renumbering of the whole list, not an incremental
    // adjustment, so rapid successive reorders can't interleave into a
    // bad intermediate state.
    normalizeDisplayOrders(list) {
      list.forEach((attr, idx) => {
        attr.display_order = idx + 1;
      });
      return list;
    },
    async saveChanges() {
      const domain = (this.selectedDomain || '').toLowerCase();
      if (!domain || this.loading || this.saving) return;
      this.saving = true;
      try {
        const payload = this.sortedAttributes.map((attr, idx) => ({
          attribute_id: attr.attribute_id,
          display_order: Number(attr.display_order) || idx + 1,
          label: attr.label,
          key: attr.key,
          type: attr.type,
          required: !!attr.required,
          editable: attr.editable !== false,
          deletable: attr.deletable !== false,
          options: attr.options || null,
          is_system: !!attr.is_system,
          previous_keys: Array.isArray(attr.previous_keys) ? [...attr.previous_keys] : [],
        }));

        const invalidKey = payload.find((attr) => !validatePersonaAttributeKey(attr.key).ok);
        if (invalidKey) {
          this.showError(this.t('personaframework.validationInvalidKey'));
          return;
        }

        const aliasConflict = findAliasedAttributeKeyConflict(payload.map((attr) => attr.key));
        if (aliasConflict) {
          this.showError(this.t('personaframework.duplicateAliasKeyError', {
            first: aliasConflict.keys[0],
            second: aliasConflict.keys[1],
          }));
          return;
        }

        const orders = payload.map((attr) => attr.display_order);
        const duplicateOrders = [...new Set(orders.filter((order, idx) => orders.indexOf(order) !== idx))];
        if (duplicateOrders.length) {
          this.showError(this.t('personaframework.duplicateOrderError', { orders: duplicateOrders.join(', ') }));
          return;
        }

        // Defensive only: drag-and-drop (via sortableAttributes' setter) and
        // delete (via confirmDeleteAttribute) both always renumber to a
        // clean 1..N, so this should never trigger from normal UI use.
        // Kept as a client-side mirror of the backend's authoritative check
        // (see save_persona_schema in domains.py) so a drifted/stale state
        // fails fast with a clear message instead of a raw 400 from the API.
        const hasInvalidRange = orders.some((order) => order < 1 || order > payload.length);
        if (hasInvalidRange) {
          this.showError(this.t('personaframework.invalidOrderRangeError'));
          return;
        }

        await save_persona_domain_schema(domain, payload);
        await this.loadSchema(domain);
        this.showSuccess(this.t('personaframework.saveSuccess'));
      } catch (error) {
        const detail = error.response?.data?.detail;
        let message = this.t('personaframework.saveError');
        if (typeof detail === 'string') {
          message = detail;
        } else if (Array.isArray(detail) && detail.length) {
          const first = detail[0];
          const validationMsg = typeof first === 'object' ? first.msg : null;
          if (validationMsg) {
            message = String(validationMsg).replace(/^Value error,\s*/i, '');
          }
        }
        this.showError(message);
      } finally {
        this.saving = false;
      }
    },
    showSuccess(message) {
      this.successMessage = message;
      this.successSnackbar = true;
    },
    showError(message) {
      this.errorMessage = message;
      this.errorSnackbar = true;
    },
  },
};
</script>

<style scoped>
.domain-select :deep(.v-field) {
  border-width: 2px;
  border-color: rgb(var(--v-theme-primary));
  border-radius: 12px;
}

/* Drag-and-drop attribute list styling. */

.col-drag {
  width: 40px;
  flex: 0 0 40px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.col-order {
  width: 64px;
  flex: 0 0 64px;
}

.col-name {
  flex: 1 1 220px;
  min-width: 0;
}

.col-type {
  width: 140px;
  flex: 0 0 140px;
}

.col-required {
  width: 110px;
  flex: 0 0 110px;
}

.col-editable {
  width: 100px;
  flex: 0 0 100px;
}

.col-deletable {
  width: 100px;
  flex: 0 0 100px;
}

.col-options {
  flex: 1 1 180px;
  min-width: 0;
}

.col-actions {
  width: 110px;
  flex: 0 0 110px;
}

.attributes-list-header {
  padding: 10px 12px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  border-bottom: 2px solid rgba(var(--v-theme-on-surface), 0.08);
}

.attributes-scroll-container {
  max-height: 620px;
  overflow-y: auto;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
  border-radius: 8px;
}

.attributes-row {
  padding: 10px 12px;
  border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.06);
  background: rgb(var(--v-theme-surface));
}

.attributes-row:last-child {
  border-bottom: none;
}

.drag-handle {
  cursor: grab;
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
}

.drag-handle:active {
  cursor: grabbing;
}

.order-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 28px;
  padding: 0 6px;
  border-radius: 6px;
  background: rgba(var(--v-theme-primary), 0.1);
  color: rgb(var(--v-theme-primary));
  font-weight: 700;
  font-size: 0.85rem;
}

/* Placeholder shown in the gap while an item is being dragged */
.drag-ghost {
  opacity: 0.4;
  background: rgba(var(--v-theme-primary), 0.06);
}

/* The row actually being dragged (follows the cursor) */
.drag-active {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}
</style>
